"use client";

import { AlgoliaEvents, useAlgoliaIndex } from "@entities/algolia";
import { Proposal } from "@entities/proposal";
import { Vacancy, VacancyableType } from "@entities/vacancy";
import { VacancyApplyAction } from "@features/vacancy-apply-action";
import { BellIcon, CalendarDateRangeIcon, MapPinIcon } from "@heroicons/react/24/solid";
import { TDomesticVacancyDetailPageMeta } from "@shared/cms";
import { PageShareButtons } from "@shared/ui";
import { formatDate, formatRelativeTime, imageFallback } from "@shared/utils";
import Image from "next/image";
import { useRouter } from "next/navigation";
import aa from "search-insights";
import { toast } from "sonner";

export function VacancyHeaderSection({
  vacancy,
  activeProposal,
  meta,
}: {
  vacancy: Vacancy;
  activeProposal?: Proposal | null;
  meta: TDomesticVacancyDetailPageMeta;
}) {
  const router = useRouter();
  const { industrialVacancy, microVacancy } = useAlgoliaIndex();

  const handleApplySucceed = () => {
    router.refresh();

    toast.success(`Berhasil melamar lowongan`, {
      duration: 2000,
      description: `Berhasil melamar lowongan ${vacancy.title}`,
    });

    aa("sendEvents", [
      {
        eventName:
          vacancy.vacancyableType === VacancyableType.MICRO_VACANCY
            ? AlgoliaEvents.MICRO_VACANCY_APPLIED
            : AlgoliaEvents.INDUSTRIAL_VACANCY_APPLIED,
        eventType: "conversion",
        index: vacancy.vacancyableType === VacancyableType.MICRO_VACANCY ? microVacancy : industrialVacancy,
        objectIDs: [
          `Karirhub\\Vacancy\\Domain\\Model\\${vacancy.vacancyableType === VacancyableType.MICRO_VACANCY ? "MicroVacancy" : "IndustrialVacancy"}::${vacancy.id}`,
        ],
      },
    ]);
  };

  return (
    <section className="relative">
      <div className="container">
        <div className="flex md:space-x-6 -mt-24 sm:-mt-[200px]">
          <div className="flex-1 flex flex-col sm:flex-row sm:items-end space-y-4 sm:space-y-0 sm:space-x-6 w-full">
            <div className="flex-1 flex flex-col w-full">
              {vacancy.vacancyableType !== VacancyableType.MICRO_VACANCY && (
                <div className="size-16 sm:size-20 overflow-hidden border border-grey-200 rounded-xl">
                  <Image
                    src={
                      vacancy.employer.logoUri ||
                      vacancy.companyLogoUri ||
                      vacancy.employer?.employerable?.logoUri ||
                      vacancy.job?.employer?.logoUri ||
                      "/blank-white.webp"
                    }
                    alt={vacancy.companyName || vacancy.employer?.name || vacancy.job?.employer?.name}
                    width="80"
                    height="80"
                    className="w-full h-full bg-white rounded-lg object-contain"
                    onError={imageFallback()}
                  />
                </div>
              )}
              <div className="mt-6">
                <h1 className="mb-3 text-gray-700 text-xl sm:text-2xl font-bold">{vacancy.title || vacancy.job?.title}</h1>
                <div className="space-y-1.5 sm:space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPinIcon className="size-5 min-w-5 text-gray-400" />
                    <div className="flex-1">{vacancy.region?.name || vacancy.job?.region?.name}</div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarDateRangeIcon className="size-5 min-w-5 text-gray-400" />
                    <div className="flex-1">
                      <span>
                        {meta.publishedAtLabel || "Diposting"}&nbsp;
                        {formatRelativeTime(vacancy.publishedAt)}
                      </span>
                      <strong className="mx-2">&bull;</strong>
                      <span>
                        {meta.availableQuotaLabel || "Jumlah lowongan"}&nbsp;
                        {vacancy.availableQuota || vacancy.quota - vacancy.accepted || 0}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 text-sm flex items-center gap-2">
                    <BellIcon className="size-5 min-w-5 text-gray-400" />
                    <div className="flex-1">
                      {meta.expiresAtLabel}&nbsp;
                      <span className="font-medium">{formatDate(vacancy.expiresAt || vacancy.job?.expireAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {!activeProposal && <VacancyApplyAction vacancy={vacancy} onSucceed={handleApplySucceed} />}
          </div>
          <div className="hidden md:block w-[264px]"></div>
        </div>
        <PageShareButtons />
      </div>
    </section>
  );
}
